/**
 * The clinic home: licence status, staff and their roles, invitations, and assigning patients
 * (PRA-002, PRA-003). A member of several clinics switches between them here. Only an owner sees
 * the controls that change staff and assignments, and the API refuses them to anyone else.
 */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import type { ClinicChoice, ClinicHome, ClinicRole, ClinicSnapshot } from "@lattice/core";
import { CLINIC_ROLES } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { Checkbox } from "@lattice/ui/Checkbox";
import { ConfirmDialog } from "@lattice/ui/ConfirmDialog";
import { EmptyState } from "@lattice/ui/EmptyState";
import { ErrorState } from "@lattice/ui/ErrorState";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { Input } from "@lattice/ui/Input";
import type { MenuItem } from "@lattice/ui/Menu";
import { Menu } from "@lattice/ui/Menu";
import type { Column } from "@lattice/ui/Table";
import { Table } from "@lattice/ui/Table";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Section } from "@lattice/ui/Section";
import { Select } from "@lattice/ui/Select";
import { Heading, Text } from "@lattice/ui/Text";
import { textStyle } from "@lattice/ui/textStyle";
import { visuallyHidden } from "@lattice/ui/visuallyHidden";
import { assignClinicPatient, chooseClinic, inviteClinicStaff, removeClinicMember, resendClinicInvite, revokeClinicInvite, updateClinicMember } from "../api.ts";
import { LeaveGuard } from "./LeaveGuard.tsx";
import { takeRemoved } from "./removed.ts";
import { RecordList, RecordRow } from "./Record.tsx";
import { ClinicShell } from "./Shell.tsx";
const styles = {
  legend: {
    kmVPX3: "x1717udv",
    k1K539: "xz4076n",
    $$css: true
  }
};
const ROLE: Readonly<Record<ClinicRole, string>> = {
  owner: "Owner",
  practitioner: "Practitioner",
  assistant: "Assistant"
};

/** "Owner and practitioner". */
function rolesLabel(roles: readonly ClinicRole[]): string {
  const names = roles.map((role, index) => index === 0 ? ROLE[role] : ROLE[role].toLowerCase());
  const last = names.pop();
  if (last === undefined) return "No role";
  return names.length === 0 ? last : `${names.join(", ")} and ${last}`;
}

/**
 * One checkbox per clinic role. The last checked box cannot be cleared, because a member always
 * holds at least one role. Removing someone is its own button.
 */
function RoleChoices({
  legend,
  hideLegend = false,
  roles,
  disabled = false,
  onChange
}: {
  readonly legend: string;
  readonly hideLegend?: boolean;
  readonly roles: readonly ClinicRole[];
  readonly disabled?: boolean;
  readonly onChange: (roles: readonly ClinicRole[]) => void;
}) {
  return <fieldset className="x78zum5 xdt5ytf xskm3c9 x1ghz6dp x1717udv xc342km xeuugli" disabled={disabled} aria-label={hideLegend ? legend : undefined}>
      {hideLegend ? null : <legend {...stylex.props(textStyle.label, styles.legend)}>{legend}</legend>}
      {CLINIC_ROLES.map(role => {
      const held = roles.includes(role);
      return <Checkbox key={role} label={ROLE[role]} checked={held} disabled={held && roles.length === 1} description={held && roles.length === 1 ? "Everyone keeps at least one role." : undefined} onChange={() => onChange(held ? roles.filter(r => r !== role) : [...roles, role])} />;
    })}
    </fieldset>;
}
const LICENCE: Readonly<Record<ClinicSnapshot["members"][number]["licenceStatus"], string>> = {
  none: "No licence",
  unverified: "Licence unverified",
  verified: "Licence verified"
};
function LicenceBanner({
  clinic
}: {
  readonly clinic: ClinicSnapshot;
}) {
  if (clinic.canSubmitFormulas) return <Banner tone="ok" title="Licence verified" testId="licence-status">
        A verified licence is on file. This clinic can submit formulas.
      </Banner>;
  return <Banner tone="warn" title="Licence not verified yet" testId="licence-status">
      Lattice staff still need to verify a licence before this clinic can submit formulas.
    </Banner>;
}

/* The browser's own email check is off with `noValidate`, so the form says it in its own words. */
function emailProblem(email: string): string | null {
  if (email.trim() === "") return "Enter an email address.";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? null : "Enter an email address like name@example.com.";
}

/** What a refused change says. A cancelled step-up is not a failure, so it says nothing changed. */
function changeProblem(code: string, what: string): string {
  if (code === "last_owner") return "A clinic needs an owner. Make someone else owner first.";
  if (code === "step_up_required") return `Nothing changed. Changing that needs a fresh authenticator code.`;
  return `We couldn't ${what.toLowerCase()}. Refresh and try again.`;
}

/** The invitation link lives 24 hours, so the time of day matters as much as the date. */
function expiryLabel(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : `Link works until ${new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date)}.`;
}
function InviteForm({
  onChange
}: {
  readonly onChange: (clinic: ClinicSnapshot) => void;
}) {
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<readonly ClinicRole[]>(["practitioner"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  async function submit() {
    setBusy(true);
    setError(null);
    const result = await inviteClinicStaff(email, roles);
    setBusy(false);
    if (result.status !== "ok") {
      setError(result.code === "step_up_required" ? "Nothing was sent. Inviting staff needs a fresh authenticator code." : "We couldn't send the invitation. Check the address and try again.");
      return;
    }
    setEmail("");
    setTried(false);
    onChange(result.clinic);
  }
  return <form noValidate aria-labelledby="invite-staff-heading" className="x78zum5 xdt5ytf x626il7" onSubmit={event => {
    event.preventDefault();
    setTried(true);
    if (emailProblem(email) !== null) focusFirstError(event.currentTarget);else if (!busy) void submit();
  }}>
      <Heading level={3} id="invite-staff-heading">
        Invite staff
      </Heading>
      <div className="x1983rqf xrvj5dj x1y6fwsi x626il7 xpqajaz">
        <Field label="Email" id="invite-email" error={tried ? emailProblem(email) : null}>
          {control => <Input {...control} type="email" autoComplete="off" value={email} onChange={setEmail} />}
        </Field>
        <RoleChoices legend="Roles" roles={roles} onChange={setRoles} />
        <Button type="submit" corners="rounded" testId="invite-staff" loading={busy} size="md">
          Send invitation
        </Button>
      </div>
      {error === null ? null : <ErrorState title="Invitation not sent" body={error} />}
      <LeaveGuard dirty={email.trim() !== ""} what="the invitation you started" />
    </form>;
}
type Member = ClinicSnapshot["members"][number];
function Members({
  clinic,
  onChange
}: {
  readonly clinic: ClinicSnapshot;
  readonly onChange: (clinic: ClinicSnapshot) => void;
}) {
  const owner = clinic.roles.includes("owner");
  const [problem, setProblem] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");
  const [removing, setRemoving] = useState<Member | null>(null);
  const [revoking, setRevoking] = useState<ClinicSnapshot["invitations"][number] | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  /* A failed Send again shows on its own row, where the person pressed it. */
  const [rowProblem, setRowProblem] = useState<{
    id: string;
    text: string;
  } | null>(null);
  /* Invitations sent again in this visit, so the row says so beside the button that did it. */
  const [resent, setResent] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const owners = clinic.members.filter(member => member.roles.includes("owner"));
  async function changeRoles(member: Member, roles: readonly ClinicRole[]) {
    setProblem(null);
    setAnnounce("");
    setSaving(member.userId);
    const result = await updateClinicMember(member.userId, roles);
    setSaving(null);
    if (result.status === "ok") {
      onChange(result.clinic);
      setAnnounce(`Saved. Roles for ${member.name || member.email} are now ${rolesLabel(roles).toLowerCase()}.`);
    } else setProblem(changeProblem(result.code, "change that role"));
  }
  async function resend(invite: ClinicSnapshot["invitations"][number]) {
    setProblem(null);
    setRowProblem(null);
    setSending(invite.id);
    const result = await resendClinicInvite(invite.id);
    setSending(null);
    if (result.status === "ok") {
      onChange(result.clinic);
      setResent(all => [...all, invite.id]);
      setAnnounce(`Invitation sent again to ${invite.email}.`);
    } else {
      const text = "We couldn't send it again. Refresh and try again.";
      setRowProblem({
        id: invite.id,
        text
      });
      setAnnounce(`${text} (${invite.email})`);
    }
  }

  /* Returns the words for the dialog to show when it did not work, or null when it did. */
  async function revoke(invite: ClinicSnapshot["invitations"][number]): Promise<string | null> {
    const result = await revokeClinicInvite(invite.id);
    if (result.status === "ok") {
      onChange(result.clinic);
      setAnnounce(`The invitation to ${invite.email} was withdrawn.`);
      return null;
    }
    return "We couldn't withdraw it. Refresh and try again.";
  }

  /* Returns the words for the dialog to show when it did not work, or null when it did. */
  async function remove(member: Member): Promise<string | null> {
    setAnnounce("");
    const result = await removeClinicMember(member.userId);
    if (result.status === "ok") {
      onChange(result.clinic);
      setAnnounce(`${member.name || member.email} was removed from the clinic.`);
      return null;
    }
    return changeProblem(result.code, "remove them");
  }
  function removeItem(member: Member): MenuItem {
    const onSelect = () => setRemoving(member);
    return owners.length === 1 && member.roles.includes("owner") ? {
      label: "Remove member",
      tone: "danger",
      onSelect,
      disabled: true,
      reason: "A clinic needs an owner. Make someone else owner first."
    } : {
      label: "Remove member",
      tone: "danger",
      onSelect
    };
  }
  const columns: readonly Column<Member>[] = [{
    id: "name",
    header: "Name",
    primary: true,
    minWidth: "11rem",
    truncate: true,
    cell: member => member.name || member.email,
    text: member => member.name || member.email
  }, {
    id: "email",
    header: "Email",
    minWidth: "11rem",
    truncate: true,
    cell: member => member.email,
    text: member => member.email
  }, {
    id: "roles",
    header: "Roles",
    minWidth: "11rem",
    cell: member => owner ? <RoleChoices legend={`Roles for ${member.email}`} hideLegend roles={member.roles} disabled={saving === member.userId} onChange={roles => void changeRoles(member, roles)} /> : rolesLabel(member.roles)
  }, {
    id: "licence",
    header: "Licence",
    cell: member => LICENCE[member.licenceStatus]
  }, ...(owner ? [{
    id: "actions",
    header: "Actions",
    align: "end",
    cell: (member: Member) => <Menu label={`Actions for ${member.email}`} items={[removeItem(member)]} />
  } as const] : [])];
  return <Section title="Staff" count={String(clinic.members.length)}>
      <Table label="Staff" columns={columns} rows={clinic.members} rowKey={member => member.userId} empty="No one else is on this clinic yet." />
      {/* On the page before it has anything to say, so a screen reader announces it, and in view so a
          sighted person sees the change was kept. */}
      <div role="status">
        {announce === "" ? null : <Text variant="caption" tone="secondary">
            {announce}
          </Text>}
      </div>
      {clinic.invitations.length === 0 ? null : <Heading level={3}>Invitations waiting ({clinic.invitations.length})</Heading>}
      {clinic.invitations.length === 0 ? null : <RecordList label={`Invitations waiting (${String(clinic.invitations.length)})`}>
          {clinic.invitations.map(invite => <RecordRow key={invite.id} title={invite.email} detail={`Invited as ${rolesLabel(invite.roles).toLowerCase()}. ${resent.includes(invite.id) ? "Sent again just now. " : ""}${expiryLabel(invite.expiresAt)}${rowProblem?.id === invite.id ? ` ${rowProblem.text}` : ""}`} trailing={owner ? <>
                    <Button tone="quiet" size="small" corners="rounded" loading={sending === invite.id} ariaLabel={`Send the invitation to ${invite.email} again`} onClick={() => void resend(invite)}>
                      Send again
                    </Button>
                    <Button variant="dangerSecondary" size="small" corners="rounded" busy={sending === invite.id} ariaLabel={`Withdraw the invitation to ${invite.email}`} onClick={() => setRevoking(invite)}>
                      Withdraw
                    </Button>
                  </> : undefined} />)}
        </RecordList>}
      {problem === null ? null : <ErrorState title="Not changed" body={problem} />}
      {owner ? <InviteForm onChange={onChange} /> : null}
      <ConfirmDialog open={revoking !== null} onClose={() => setRevoking(null)} title={`Withdraw the invitation to ${revoking?.email ?? "this person"}?`} tone="danger" confirmLabel="Withdraw invitation" cancelLabel="Keep invitation" testId="revoke-dialog" onConfirm={() => revoking === null ? Promise.resolve(null) : revoke(revoking)}>
        The link in their email stops working. You can invite them again.
      </ConfirmDialog>
      <ConfirmDialog open={removing !== null} onClose={() => setRemoving(null)} title={`Remove ${removing?.name || removing?.email || "this person"} from the clinic?`} confirmLabel="Remove member" cancelLabel="Keep member" tone="danger" onConfirm={() => removing === null ? Promise.resolve(null) : remove(removing)}>
        They lose access to this clinic's patients straight away. Their own account is not deleted.
      </ConfirmDialog>
    </Section>;
}
function Patients({
  clinic,
  onChange
}: {
  readonly clinic: ClinicSnapshot;
  readonly onChange: (clinic: ClinicSnapshot) => void;
}) {
  const practitioners = clinic.members.filter(member => member.roles.includes("practitioner"));
  const owner = clinic.roles.includes("owner");
  const [removed] = useState(takeRemoved);
  const [problem, setProblem] = useState<string | null>(null);
  async function assign(linkId: string, practitionerUserId: string) {
    setProblem(null);
    const result = await assignClinicPatient(linkId, practitionerUserId);
    if (result.status === "ok") onChange(result.clinic);else setProblem("We couldn't assign that practitioner. Refresh and try again.");
  }
  return <Section title="Patients" count={String(clinic.patients.length)} focusTitle={removed !== null} actions={<LinkButton href="/clinic/patients" tone="quiet" corners="rounded" size="md">
          Add a patient
        </LinkButton>}>
      <div role="status">
        {removed === null ? null : <Text tone="secondary" testId="patient-removed">
            {removed} was removed from this clinic.
          </Text>}
      </div>
      {clinic.patients.length === 0 ? <EmptyState title="No patients are linked to this clinic yet." body="Add one to start their formula." /> : <RecordList label="Patients">
          {clinic.patients.map(patient => <RecordRow key={patient.linkId} title={patient.name ?? "Name not given"} detail={patient.practitionerName === null ? "No practitioner assigned" : patient.practitionerName}>
              {owner ? <span className="xeuugli x193iq5w">
                  <Select aria-label={`Practitioner for ${patient.name ?? "this patient"}`} options={[{
            value: "",
            label: "None"
          }, ...practitioners.map(member => ({
            value: member.userId,
            label: member.name || member.email
          }))]} value={patient.practitionerUserId ?? ""} onChange={value => void assign(patient.linkId, value)} />
                </span> : null}
            </RecordRow>)}
        </RecordList>}
      {problem === null ? null : <ErrorState title="Not assigned" body={problem} />}
    </Section>;
}
const SWITCH_FAILED = "We couldn't switch clinics. You are still in the clinic shown. Try again.";

/** Moves this session to another of the person's clinics. Shown only to members of several. */
function ClinicSwitcher({
  current,
  clinics,
  onSwitch
}: {
  readonly current: string;
  readonly clinics: readonly ClinicChoice[];
  readonly onSwitch: (home: ClinicHome) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [now, setNow] = useState("");
  if (clinics.length < 2) return null;
  return <>
      <span role="status" {...stylex.props(visuallyHidden.hidden)}>
        {now}
      </span>
      <Field label="Clinic" id="clinic-switch" error={problem}>
        {control => <Select {...control} options={clinics.map(choice => ({
        value: choice.id,
        label: choice.name
      }))} value={current} onChange={value => {
        /* Ignored while a switch runs, rather than disabled, so focus stays on the select. */
        if (busy) return;
        setBusy(true);
        setProblem(null);
        void chooseClinic(value).then(loaded => {
          setBusy(false);
          if (loaded.status === "ok") {
            setNow(`Now viewing ${clinics.find(c => c.id === value)?.name ?? "the clinic"}`);
            onSwitch(loaded.home);
          } else setProblem(SWITCH_FAILED);
        }, () => {
          setBusy(false);
          setProblem(SWITCH_FAILED);
        });
      }} />}
      </Field>
    </>;
}
export function Portal({
  clinic,
  clinics,
  onChange,
  onSwitch
}: {
  readonly clinic: ClinicSnapshot;
  readonly clinics: readonly ClinicChoice[];
  readonly onChange: (clinic: ClinicSnapshot) => void;
  readonly onSwitch: (home: ClinicHome) => void;
}) {
  return <ClinicShell title={clinic.organization.name} signedIn clinicName={clinic.organization.name}>
      <ClinicSwitcher current={clinic.organization.id} clinics={clinics} onSwitch={onSwitch} />
      <div className="x78zum5 xdt5ytf x626il7">
        <Text tone="secondary" testId="your-roles">
          You are {rolesLabel(clinic.roles).toLowerCase()} here.
        </Text>
        <LicenceBanner clinic={clinic} />
      </div>
      <Members clinic={clinic} onChange={onChange} />
      <Patients clinic={clinic} onChange={onChange} />
    </ClinicShell>;
}